import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Skeleton,
    Avatar,
    Grid,
    IconButton,
    Drawer,
    Divider,
    Button,
    InputAdornment,
    Badge
} from '@mui/material';
import {
    ContentCopy,
    Visibility,
    ArrowUpward,
    ArrowDownward,
    AccountBalanceWallet,
    Search as SearchIcon,
    FilterList
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAdminTransactions } from 'hooks/useTransactions';
import FilterDrawer from 'components/common/FilterDrawer';

export default function TransactionsLog() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Filters
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [search, setSearch] = useState('');
    const [selectedTx, setSelectedTx] = useState(null);

    const { data: transRes, isLoading } = useAdminTransactions({
        page: page + 1,
        limit: rowsPerPage,
        type: filters.type !== 'ALL' ? filters.type : undefined,
        search: search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
    });

    const transactions = transRes?.data || [];
    const total = transRes?.total || 0;
    const skeletonRows = Array.from({ length: 8 });

    const stats = {
        totalInflow: transactions.filter((t) => t.type === 'CREDIT').reduce((acc, curr) => acc + curr.amount, 0),
        totalOutflow: transactions.filter((t) => t.type === 'DEBIT').reduce((acc, curr) => acc + curr.amount, 0)
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setPage(0);
    };

    const filterConfig = [
        {
            name: 'type',
            label: 'Type',
            type: 'select',
            options: [
                { label: 'All Types', value: 'ALL' },
                { label: 'Credits Only', value: 'CREDIT' },
                { label: 'Debits Only', value: 'DEBIT' }
            ],
            defaultValue: 'ALL'
        },
        { name: 'startDate', label: 'Start Date', type: 'date' },
        { name: 'endDate', label: 'End Date', type: 'date' }
    ];

    const activeFiltersCount = Object.keys(filters).length;

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Typography variant="h4" fontWeight={700} mb={4}>
                Financial Audit Trail
            </Typography>

            {/* Statistics Section */}
            <Grid container spacing={3} mb={4}>
                <StatCard title="Total Credits (Page)" value={stats.totalInflow} color="#2e7d32" icon={<ArrowUpward color="success" />} />
                <StatCard title="Total Debits (Page)" value={stats.totalOutflow} color="#d32f2f" icon={<ArrowDownward color="error" />} />
                <StatCard
                    title="Net Volume (Page)"
                    value={stats.totalInflow - stats.totalOutflow}
                    color="#1976d2"
                    icon={<AccountBalanceWallet color="primary" />}
                />
            </Grid>

            <Paper sx={{ width: '100%', overflow: 'hidden', padding: 2, borderRadius: 2 }}>
                <Stack direction="row" spacing={2} mb={3} alignItems="center">
                    <TextField
                        placeholder="Search Reference, Name, or Email..."
                        size="small"
                        value={search}
                        onChange={handleSearchChange}
                        sx={{ width: '350px' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Badge badgeContent={activeFiltersCount} color="primary">
                            <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterOpen(true)}>
                                Filters
                            </Button>
                        </Badge>
                        {activeFiltersCount > 0 && (
                            <Button variant="text" onClick={() => setFilters({})} color="error" size="small">
                                Reset
                            </Button>
                        )}
                    </Stack>

                    <Box sx={{ flexGrow: 1 }} />
                </Stack>

                <FilterDrawer
                    open={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    onApply={handleFilterApply}
                    initialFilters={filters}
                    config={filterConfig}
                />

                <TableContainer sx={{ maxHeight: 650 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>User / Party</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Transaction ID</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    Action
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                skeletonRows.map((_, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell colSpan={7}>
                                            <Skeleton variant="text" height={40} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : transactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No transactions found matching your criteria.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                transactions.map((tx) => (
                                    <TableRow hover key={tx.id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {format(new Date(tx.createdAt), 'dd MMM')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {format(new Date(tx.createdAt), 'hh:mm a')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Avatar
                                                    sx={{
                                                        width: 34,
                                                        height: 34,
                                                        bgcolor: tx.role === 'rider' ? '#e3f2fd' : '#f3e5f5',
                                                        color: tx.role === 'rider' ? '#1976d2' : '#9c27b0'
                                                    }}
                                                >
                                                    {tx.user?.firstName?.[0]}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ lineHeight: 1.2, textTransform: 'capitalize' }}>
                                                        {tx.user?.firstName} {tx.user?.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {tx.user?.email}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                color={tx.type === 'CREDIT' ? 'success.main' : 'error.main'}
                                            >
                                                {tx.type === 'CREDIT' ? '+' : '-'} ₦{tx.amount.toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        color: 'text.secondary',
                                                        bgcolor: '#f0f0f0',
                                                        px: 0.5,
                                                        borderRadius: 0.5
                                                    }}
                                                >
                                                    {tx.reference.substring(0, 10)}...
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 180 }} noWrap title={tx.remark}>
                                                {tx.remark || '---'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={tx.status}
                                                size="small"
                                                sx={{
                                                    bgcolor: '#e8f5e9',
                                                    color: '#2e7d32',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    textTransform: 'uppercase'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton color="primary" onClick={() => setSelectedTx(tx)}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            <TransactionDetailDrawer open={!!selectedTx} tx={selectedTx} onClose={() => setSelectedTx(null)} />
        </Box>
    );
}

function StatCard({ title, value, icon, color }) {
    return (
        <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {title.toUpperCase()}
                    </Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color }}>
                        {isNaN(value) ? '...' : `₦${value.toLocaleString()}`}
                    </Typography>
                </Box>
                <Avatar sx={{ bgcolor: `${color}15`, p: 1 }}>{icon}</Avatar>
            </Paper>
        </Grid>
    );
}

StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.number,
    icon: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired
};

function TransactionDetailDrawer({ open, tx, onClose }) {
    if (!tx) return null;
    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 400, p: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={1}>
                    Transaction Details
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box mb={2}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Full Reference ID
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, flexGrow: 1, wordBreak: 'break-all' }}
                        >
                            {tx.reference}
                        </Typography>
                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(tx.reference)}>
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>

                <DetailItem label="User" value={`${tx.user?.firstName} ${tx.user?.lastName} (${tx.user?.email})`} />
                <DetailItem label="Role" value={tx.role} isChip />
                <Divider sx={{ my: 2 }} />
                <DetailItem
                    label="Amount"
                    value={`₦${tx.amount.toLocaleString()}`}
                    color={tx.type === 'CREDIT' ? 'success.main' : 'error.main'}
                />
                <DetailItem label="Type" value={tx.type} />
                <DetailItem label="Remark/Description" value={tx.remark} />
                <DetailItem label="Timestamp" value={format(new Date(tx.createdAt), 'PPPP p')} />
            </Box>
        </Drawer>
    );
}

TransactionDetailDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    tx: PropTypes.object,
    onClose: PropTypes.func.isRequired
};

function DetailItem({ label, value, isChip, color }) {
    return (
        <Box mb={2}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            {isChip ? (
                <Chip label={value} size="small" variant="outlined" sx={{ mt: 0.5, textTransform: 'uppercase' }} />
            ) : (
                <Typography variant="body2" fontWeight={600} color={color || 'text.primary'}>
                    {value || 'N/A'}
                </Typography>
            )}
        </Box>
    );
}

DetailItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    isChip: PropTypes.bool,
    color: PropTypes.string
};
